/**
 * MotorSound — procedural adaptive engine sound for the robot.
 *
 * Signal chain:
 *
 *   baseOsc  (sawtooth, ~40–90 Hz rumble)  ─────────────────────────┐
 *   whineOsc (sawtooth, ~200–700 Hz gear whine) ─── whineGain ───────┤
 *   turretOsc (sine, ~800–1400 Hz servo whine)  ─── turretGain ──────┤
 *   noiseSource (white noise loop) ─── noiseLpf ─── noiseGain ───────┤
 *                                                                     ▼
 *                                                   masterGain ── sfxChain ── destination
 *
 * All parameter changes use setTargetAtTime so nothing ever clicks.
 */

import type { SoundSystem } from './SoundSystem';

const SMOOTHING = 0.08; // seconds for parameter ramps (time constant)

// Noise buffer length (2 seconds of white noise, looped)
const NOISE_DURATION = 2;

export class MotorSound {
  private ctx: AudioContext;
  private masterGain: GainNode;

  private baseOsc: OscillatorNode;
  private baseGain: GainNode;

  private whineOsc: OscillatorNode;
  private whineGain: GainNode;

  private turretOsc: OscillatorNode;
  private turretGain: GainNode;

  private noiseSource: AudioBufferSourceNode;
  private noiseLpf: BiquadFilterNode;
  private noiseGain: GainNode;

  // Previous robot state for delta computation
  private prevX = 0;
  private prevY = 0;
  private prevAngle = 0;
  private prevTurretAngle = 0;
  private started = false;
  // Pending stop-fadeout cleanup. If `start()` is called before the
  // fadeout timer fires, we must cancel it — otherwise the scheduled
  // `.stop()` calls would tear down the oscillators we just restarted.
  private pendingStopTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(sound: SoundSystem) {
    this.ctx = sound.getAudioContext();

    // ── Master gain (routes into SFX mute/volume chain) ───────
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0;
    this.masterGain.connect(sound.getSfxMuteGain());

    // ── Base oscillator (body/track rumble) ───────────────────
    this.baseOsc = this.ctx.createOscillator();
    this.baseOsc.type = 'sawtooth';
    this.baseOsc.frequency.value = 42;
    this.baseGain = this.ctx.createGain();
    this.baseGain.gain.value = 0.18;
    // Shape: low-pass to remove harsh highs
    const baseLpf = this.ctx.createBiquadFilter();
    baseLpf.type = 'lowpass';
    baseLpf.frequency.value = 260;
    baseLpf.Q.value = 1.2;
    this.baseOsc.connect(baseLpf);
    baseLpf.connect(this.baseGain);
    this.baseGain.connect(this.masterGain);

    // ── Whine oscillator (drivetrain / gear harmonic) ─────────
    this.whineOsc = this.ctx.createOscillator();
    this.whineOsc.type = 'sawtooth';
    this.whineOsc.frequency.value = 220;
    this.whineGain = this.ctx.createGain();
    this.whineGain.gain.value = 0;
    const whineBpf = this.ctx.createBiquadFilter();
    whineBpf.type = 'bandpass';
    whineBpf.frequency.value = 400;
    whineBpf.Q.value = 3;
    this.whineOsc.connect(whineBpf);
    whineBpf.connect(this.whineGain);
    this.whineGain.connect(this.masterGain);

    // ── Turret oscillator (servo / pivot whine) ───────────────
    this.turretOsc = this.ctx.createOscillator();
    this.turretOsc.type = 'sine';
    this.turretOsc.frequency.value = 900;
    this.turretGain = this.ctx.createGain();
    this.turretGain.gain.value = 0;
    this.turretOsc.connect(this.turretGain);
    this.turretGain.connect(this.masterGain);

    // ── White noise (mechanical texture / track clatter) ──────
    const noiseBuffer = this.makeNoiseBuffer();
    this.noiseSource = this.ctx.createBufferSource();
    this.noiseSource.buffer = noiseBuffer;
    this.noiseSource.loop = true;
    this.noiseLpf = this.ctx.createBiquadFilter();
    this.noiseLpf.type = 'lowpass';
    this.noiseLpf.frequency.value = 400;
    this.noiseGain = this.ctx.createGain();
    this.noiseGain.gain.value = 0;
    this.noiseSource.connect(this.noiseLpf);
    this.noiseLpf.connect(this.noiseGain);
    this.noiseGain.connect(this.masterGain);
  }

  /** Call once the AudioContext is running (after first user gesture) */
  start(x: number, y: number, angle: number, turretAngle: number) {
    // If a stop-fadeout is in flight, cancel it — otherwise oscillators we
    // just restarted would be torn down when the timer fires.
    if (this.pendingStopTimer !== null) {
      clearTimeout(this.pendingStopTimer);
      this.pendingStopTimer = null;
    }
    if (this.started) return;
    this.started = true;
    this.prevX = x;
    this.prevY = y;
    this.prevAngle = angle;
    this.prevTurretAngle = turretAngle;

    this.baseOsc.start();
    this.whineOsc.start();
    this.turretOsc.start();
    this.noiseSource.start();

    // Fade master in gently on first start
    this.masterGain.gain.setTargetAtTime(1.0, this.ctx.currentTime, 0.3);
  }

  stop() {
    if (!this.started) return;
    const t = this.ctx.currentTime;
    this.masterGain.gain.setTargetAtTime(0, t, 0.2);
    // Clean up after fade. If stop() is called twice, or start() is called
    // during the fadeout, the previous timer is cancelled first.
    if (this.pendingStopTimer !== null) clearTimeout(this.pendingStopTimer);
    this.pendingStopTimer = setTimeout(() => {
      this.pendingStopTimer = null;
      try {
        this.baseOsc.stop(); this.whineOsc.stop();
        this.turretOsc.stop(); this.noiseSource.stop();
        this.baseOsc.disconnect();
        this.whineOsc.disconnect();
        this.turretOsc.disconnect();
        this.noiseSource.disconnect();
        this.masterGain.disconnect();
      } catch { /* already stopped */ }
    }, 600);
    this.started = false;
  }

  /**
   * Called every frame from LabyrinthState.update() AFTER robot.update().
   * dt in seconds.
   */
  update(
    dt: number,
    x: number, y: number,
    angle: number, turretAngle: number,
    state: 'exploring' | 'combat' | 'looting' | 'moving_to_marker' | 'returning',
  ) {
    if (!this.started || dt <= 0) return;

    const now = this.ctx.currentTime;
    const smooth = SMOOTHING;

    // ── Movement speed (tiles/sec, capped) ────────────────────
    const dx = x - this.prevX;
    const dy = y - this.prevY;
    const rawSpeed = Math.hypot(dx, dy) / dt;          // tiles/sec
    const moveSpeed = Math.min(rawSpeed, 5.0);          // cap at 5 t/s

    // ── Turn rate (rad/sec) ───────────────────────────────────
    let dAngle = angle - this.prevAngle;
    // Wrap to [-π, π] (modular arithmetic — safe against Infinity)
    dAngle = dAngle % (Math.PI * 2);
    if (dAngle > Math.PI) dAngle -= Math.PI * 2;
    if (dAngle < -Math.PI) dAngle += Math.PI * 2;
    const turnRate = Math.min(Math.abs(dAngle) / dt, Math.PI * 3);

    // ── Turret sweep rate (rad/sec) ───────────────────────────
    let dTurret = turretAngle - this.prevTurretAngle;
    dTurret = dTurret % (Math.PI * 2);
    if (dTurret > Math.PI) dTurret -= Math.PI * 2;
    if (dTurret < -Math.PI) dTurret += Math.PI * 2;
    const turretRate = Math.min(Math.abs(dTurret) / dt, Math.PI * 4);

    this.prevX = x; this.prevY = y;
    this.prevAngle = angle; this.prevTurretAngle = turretAngle;

    // ── Map state to a normalised "activity" (0–1) ───────────
    const isMoving = moveSpeed > 0.05;
    const inCombat = state === 'combat';
    const idleLevel = inCombat ? 0.35 : (state === 'looting' ? 0.15 : 0.2);
    const activity = isMoving
      ? idleLevel + (1 - idleLevel) * Math.min(moveSpeed / 4.5, 1)
      : idleLevel;

    // ── Base oscillator ───────────────────────────────────────
    // Freq: 42 Hz idle → 88 Hz full speed
    const baseFreq = 42 + activity * 46;
    this.baseOsc.frequency.setTargetAtTime(baseFreq, now, smooth);
    // Volume rises with speed, always audible
    const baseVol = 0.06 + activity * 0.14;
    this.baseGain.gain.setTargetAtTime(baseVol, now, smooth);

    // ── Whine oscillator ──────────────────────────────────────
    // Pitch: 3rd harmonic of base, rises with speed
    const whineFreq = baseFreq * 3 + turnRate * 20;
    this.whineOsc.frequency.setTargetAtTime(whineFreq, now, smooth);
    // Volume: only present when moving or turning
    const whineVol = isMoving
      ? 0.025 + (moveSpeed / 5) * 0.055 + turnRate * 0.008
      : turnRate * 0.006;
    this.whineGain.gain.setTargetAtTime(whineVol, now, smooth);

    // ── Noise (track texture) ─────────────────────────────────
    // LPF cutoff rises with speed (more broadband clatter at speed)
    const noiseCutoff = 200 + moveSpeed * 140;
    this.noiseLpf.frequency.setTargetAtTime(noiseCutoff, now, smooth);
    const noiseVol = isMoving ? 0.04 + (moveSpeed / 5) * 0.07 : 0.01;
    this.noiseGain.gain.setTargetAtTime(noiseVol, now, smooth);

    // ── Turret servo whine ────────────────────────────────────
    // Short piercing tone when turret rotates quickly
    const turretFreq = 850 + turretRate * 60;
    this.turretOsc.frequency.setTargetAtTime(turretFreq, now, 0.03);
    const turretVol = turretRate > 0.1 ? Math.min(turretRate / (Math.PI * 3), 1) * 0.04 : 0;
    this.turretGain.gain.setTargetAtTime(turretVol, now, 0.04);
  }

  // ── Helpers ───────────────────────────────────────────────────
  private makeNoiseBuffer(): AudioBuffer {
    const sampleRate = this.ctx.sampleRate;
    const frameCount = sampleRate * NOISE_DURATION;
    const buffer = this.ctx.createBuffer(1, frameCount, sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < frameCount; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    return buffer;
  }
}
